/**
 * Content ROI Service
 *
 * API client for Content ROI Analytics endpoints.
 * Enhanced with Google metrics integration (GSC positions, GA4 engagement).
 *
 * GitHub Issue: #369 - Content ROI Analytics
 * GitHub Issue: #378 - Google Enterprise Integration
 * Phase 4: Dashboard UI
 */

import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';

// Google Metrics Types (from Google Intelligence integration)
export interface GoogleMetrics {
  gsc: GSCMetrics | null;
  ga4: GA4Metrics | null;
  hasGoogleData: boolean;
}

export interface GSCMetrics {
  impressions: number;
  clicks: number;
  ctr: number;
  avgPosition: number;
  positionTrend: 'improving' | 'stable' | 'declining' | null;
  dateRange: { start: string; end: string };
}

export interface GA4Metrics {
  sessions: number;
  users: number;
  pageViews: number;
  bounceRate: number;
  avgEngagementTime: number;
  conversions: number;
  dateRange: { start: string; end: string };
}

export interface GoogleROIComparison {
  before: {
    gsc: GSCMetrics | null;
    ga4: GA4Metrics | null;
  };
  after: {
    gsc: GSCMetrics | null;
    ga4: GA4Metrics | null;
  };
  changes: {
    positionChange: number | null;
    impressionChange: number | null;
    clickChange: number | null;
    ctrChange: number | null;
    bounceRateChange: number | null;
    engagementTimeChange: number | null;
    sessionChange: number | null;
  };
}

// Types
export interface ContentROISummary {
  pagesTracked: number;
  totalDeployments: number;
  totalValueCreatedEur: number;
  competitorsOvertaken: number;
  avgLlmScoreBefore: string | null;
  avgLlmScoreAfter: string | null;
  keywordsInTop10: number;
}

export interface TopPage {
  id: string;
  url: string;
  title: string;
  total_value_eur: string;
}

export interface TimelineEvent {
  id: string;
  page_id: string;
  site_id: string;
  deployment_id: string;
  event_type: string;
  event_timestamp: string;
  event_data: {
    keyword?: string;
    oldPosition?: number;
    newPosition?: number;
    positionDelta?: number;
    snapshotType?: string;
    wordCount?: number;
    contentHash?: string;
    createdBy?: string;
  };
  impact_value_eur: string | null;
  created_at: string;
  page_url: string;
  page_title: string;
}

export interface SiteDashboardData {
  summary: ContentROISummary;
  topPages: TopPage[];
  recentTimeline: TimelineEvent[];
}

export interface KeywordTracking {
  id: string;
  keyword: string;
  search_volume_monthly: number;
  cpc_eur: string;
  competition_level: string;
  position_before: number;
  position_after: number;
  position_current: number;
  traffic_before: number;
  traffic_after: number;
  value_before_eur: string;
  value_after_eur: string;
  value_delta_eur: string;
  first_improvement_date: string | null;
  url?: string;
  title?: string;
  // Position source tracking (from Google integration)
  metadata?: {
    position_source?: 'gsc' | 'serp' | 'manual' | 'estimate';
    last_sync?: string;
    gsc_impressions?: number;
    gsc_clicks?: number;
    gsc_ctr?: number;
  };
}

export interface PageSnapshot {
  id: string;
  page_id: string;
  site_id: string;
  snapshot_type: 'before' | 'after';
  deployment_id: string;
  content_hash: string;
  word_count: number;
  avg_time_on_page_seconds: number | null;
  bounce_rate: number | null;
  page_views_30d: number | null;
  serp_positions: Record<string, number | null>;
  created_at: string;
}

export interface LLMEvaluation {
  id: string;
  llm_provider: string;
  llm_model: string;
  clarity_score: string;
  completeness_score: string;
  trust_score: string;
  purchase_intent_score: string;
  technical_depth_score: string;
  differentiation_score: string;
  overall_score: string;
  summary: string;
  unanswered_questions: string[];
  cost_cents: string;
  processing_time_ms: number;
  created_at: string;
}

export interface PageDashboardData {
  summary: {
    page: {
      id: string;
      url: string;
      title: string;
    };
    valueCreated: {
      totalEurPerMonth: number;
      percentageChange: string | null;
    };
    positionImprovements: {
      keywordsTracked: number;
      keywordsInTop10: number;
      avgImprovement: string;
    };
    llmScore: {
      before: number | null;
      after: number | null;
      delta: number | null;
    };
    engagement: {
      timeOnPageBefore: number | null;
      bounceRateBefore: number | null;
    };
    competitorsOvertaken: number;
    lastDeployment: string | null;
  };
  snapshots: PageSnapshot[];
  evaluations: LLMEvaluation[];
  keywordValue: {
    keywords: KeywordTracking[];
    total: number;
  };
  timeline: TimelineEvent[];
  overtakes: Array<{
    id: string;
    keyword: string;
    competitor_domain: string;
    our_position_before: number;
    our_position_after: number;
    competitor_position: number;
    keyword_value_eur: string;
    overtake_date: string;
  }>;
  questionProximity: Array<{
    id: string;
    question_text: string;
    similarity_before: number;
    similarity_after: number;
    similarity_delta: number;
    question_value_eur: string;
  }>;
  // Google metrics (if site has Google integrations enabled)
  googleMetrics?: GoogleMetrics;
  googleComparison?: GoogleROIComparison;
}

export interface CTRCurve {
  positions: Record<string, number>;
  extended: Record<string, number>;
  source: string;
}

// API Functions

/**
 * Fetch site-level ROI dashboard
 */
export async function fetchSiteDashboard(siteId: string): Promise<SiteDashboardData> {
  const response = await authFetch(`${API_URL}/content-roi/dashboard/site/${siteId}`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch site dashboard');
  }
  return data.data;
}

/**
 * Fetch page-level ROI dashboard
 */
export async function fetchPageDashboard(pageId: string): Promise<PageDashboardData> {
  const response = await authFetch(`${API_URL}/content-roi/dashboard/${pageId}`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch page dashboard');
  }
  return data.data;
}

/**
 * Fetch CTR curve data
 */
export async function fetchCTRCurve(): Promise<CTRCurve> {
  const response = await authFetch(`${API_URL}/content-roi/value/ctr-curve`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch CTR curve');
  }
  return data.data;
}

/**
 * Create a deployment snapshot
 */
export async function createSnapshot(
  pageId: string,
  snapshotType: 'before' | 'after',
  deploymentId?: string
): Promise<PageSnapshot> {
  const response = await authFetch(`${API_URL}/content-roi/snapshots`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pageId, snapshotType, deploymentId })
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to create snapshot');
  }
  return data.data;
}

/**
 * Trigger LLM evaluation for a page
 */
export async function triggerEvaluation(
  pageId: string,
  providers: string[] = ['openai']
): Promise<{
  evaluations: LLMEvaluation[];
  errors: Array<{ provider: string; error: string }>;
}> {
  const response = await authFetch(`${API_URL}/content-roi/evaluate/${pageId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ providers })
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to trigger evaluation');
  }
  return data.data;
}

/**
 * Track keywords for a page
 */
export async function trackKeywords(
  pageId: string,
  keywords: Array<{ keyword: string; position: number }>,
  deploymentId?: string
): Promise<KeywordTracking[]> {
  const response = await authFetch(`${API_URL}/content-roi/value/${pageId}/track`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ keywords, deploymentId })
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to track keywords');
  }
  return data.data.keywords;
}

/**
 * Get page value summary
 */
export async function fetchPageValueSummary(pageId: string): Promise<{
  keywords: KeywordTracking[];
  summary: {
    totalValueDeltaEur: number;
    keywordsTracked: number;
    keywordsImproved: number;
    keywordsInTop10: number;
  };
}> {
  const response = await authFetch(`${API_URL}/content-roi/value/${pageId}`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch page value');
  }
  return data.data;
}

/**
 * Sync keyword values from SERP tracking
 */
export async function syncFromSerpTracking(siteId: string): Promise<{
  synced: number;
  keywords: KeywordTracking[];
}> {
  const response = await authFetch(`${API_URL}/content-roi/value/sync/${siteId}`, {
    method: 'POST'
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to sync keywords');
  }
  return data.data;
}

/**
 * Sync keyword values from Google Search Console (if connected)
 * Falls back to SERP tracking when GSC is not available
 */
export async function syncFromGSCOrFallback(siteId: string): Promise<{
  synced: number;
  keywords: KeywordTracking[];
  source: 'gsc' | 'serp' | 'manual' | 'estimate';
  message: string;
}> {
  const response = await authFetch(`${API_URL}/content-roi/value/site/${siteId}/sync-gsc`, {
    method: 'POST'
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to sync keywords from GSC');
  }
  return data.data;
}

/**
 * Calculate value for a hypothetical keyword/position
 */
export async function calculateKeywordValue(
  keyword: string,
  position: number,
  searchVolume?: number,
  cpcEur?: number
): Promise<{
  keyword: string;
  position: number;
  ctr: number;
  searchVolume: number;
  cpcEur: number;
  monthlyTraffic: number;
  monthlyValueEur: number;
}> {
  const params = new URLSearchParams({
    keyword,
    position: position.toString(),
    ...(searchVolume && { searchVolume: searchVolume.toString() }),
    ...(cpcEur && { cpcEur: cpcEur.toString() })
  });

  const response = await authFetch(`${API_URL}/content-roi/value/calculate?${params}`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to calculate value');
  }
  return data.data;
}

/**
 * Fetch snapshots for a page
 */
export async function fetchPageSnapshots(pageId: string): Promise<PageSnapshot[]> {
  const response = await authFetch(`${API_URL}/content-roi/snapshots/page/${pageId}`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch snapshots');
  }
  return data.snapshots;
}

// ============================================================================
// Google Metrics Integration (GitHub Issue #378)
// ============================================================================

/**
 * Fetch current Google metrics for a page (GSC + GA4)
 * Returns null for metrics if Google integration is not connected
 */
export async function fetchPageGoogleMetrics(
  siteId: string,
  pageUrl: string
): Promise<GoogleMetrics> {
  try {
    const params = new URLSearchParams({ siteId, pageUrl });
    const response = await authFetch(`${API_URL}/content-roi/google-metrics?${params}`);
    const data = await response.json();
    if (!response.ok) {
      // Not an error - just means no Google integration
      return { gsc: null, ga4: null, hasGoogleData: false };
    }
    return data.data;
  } catch {
    return { gsc: null, ga4: null, hasGoogleData: false };
  }
}

/**
 * Fetch Google metrics comparison (before/after deployment)
 * Compares GSC position and GA4 engagement before and after a deployment date
 */
export async function fetchGoogleROIComparison(
  siteId: string,
  pageUrl: string,
  deploymentDate: string
): Promise<GoogleROIComparison | null> {
  try {
    const params = new URLSearchParams({ siteId, pageUrl, deploymentDate });
    const response = await authFetch(`${API_URL}/content-roi/google-comparison?${params}`);
    const data = await response.json();
    if (!response.ok) {
      return null;
    }
    return data.data;
  } catch {
    return null;
  }
}

/**
 * Check if site has Google integrations enabled
 */
export async function checkGoogleIntegrationStatus(siteId: string): Promise<{
  gscConnected: boolean;
  ga4Connected: boolean;
}> {
  try {
    const response = await authFetch(`${API_URL}/integrations/google/status?siteId=${siteId}`);
    const data = await response.json();
    if (!response.ok) {
      return { gscConnected: false, ga4Connected: false };
    }
    return {
      gscConnected: data.services?.search_console?.connected || false,
      ga4Connected: data.services?.analytics?.connected || false,
    };
  } catch {
    return { gscConnected: false, ga4Connected: false };
  }
}
